/*
 * This module is in charge of presenting users with results in close proximity to their current location.
 * When users select this option, they are prompted to provide permission for access to their current location.
 * Upon receiving permission, NewHomeSource then displays results near to the user's exact location.
*/
import { store, store$ } from '@redux/store';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { updateMapCenter, updateMinimumZoomLevel } from 'libraries/mapLibrary';
import { mapStateActions } from 'features/searchResultsMap';
import { searchParametersActions } from 'features/searchResultsFacets';
import { selectCurrentLocationSearchInformation } from '../redux/selectors';

/*
    Settings to consider for aproximity searches to optimize the site's performance.
    For instance, we tune the map's zoom level in advance of initiating a rectangular area search
    to prevent overloading the database with a large area from which to retrieve results.
*/
const currentLocationSearchSettings = {
    maxZoom: 9,
    latitude: null,
    longitude: null,
};

const mapViewButton = document.querySelector("#btn-map-view");

/**
 * fetchCurrentLocationResults: takes the coordinates of the map's current viewport and uses them to
 * update the location parameters from the state which will trigger an update on the page results.
 */
const fetchCurrentLocationResults = () => {
    store.dispatch(mapStateActions.enableSearchWithinMap({}));
};

/**
 * toggleCurrentLocationSearchFlag: dispatches an action to the search parameters state indicating wether the user
 * is driving a current location search.
 * @param currentSearchParameters - object representing the search parameters from the store.
 */
const toggleCurrentLocationSearchFlag = (isCurrentLocationSearch) => {
    store.dispatch(searchParametersActions.toggleCurrentLocationSearch(isCurrentLocationSearch));
};

/**
 * displayMapView: in small devices, we don't show the map untill we click the toggle map view button.
 * When doing a proximity search, we need to show the map instead of list view by design, so we toggle
 * views in this function.
 */
const displayMapView = () => {
    if (mapViewButton?.dataset.state === 'map') {
        mapViewButton?.click();
    }
}

/**
 * handleLocationPermissionGranted: when the user grants NewHomeSource access to their current location,
 * this function receives the location information. If the user's location is within the United States of America,
 * the map center will adjust to the user's current location."
 * @param geolocation - object representing the user's current position in terms of navigator.geolocation API.
 */
const handleLocationPermissionGranted = async (geolocation) => {
    currentLocationSearchSettings.latitude = geolocation.coords.latitude;
    currentLocationSearchSettings.longitude = geolocation.coords.longitude;
    displayMapView();
    sessionStorage.setItem('geolocationPermission', true);
    toggleCurrentLocationSearchFlag(true);
}

/**
 * handleLocationPermissionDenied: when the user dennies NewHomeSource access to their current location,
 * this function handles the next steps to follow like showing an information message indicating that
 * permissions where not granted.
 */
const handleLocationPermissionDenied = () => {
    sessionStorage.setItem('geolocationPermission', false);
    const errorContainer = document.querySelector('[data-current-location-permission-denied-error]');
    const searchInput = errorContainer.closest('form');

    searchInput.classList.add('error');
    errorContainer.style.display = 'block';
}

/**
 * getCurrentLocation: Requests permission from the user to access their current location and executes
 * a callback for both granted and denied permission scenarios.
 */
const getCurrentLocation = (event) => {
    if (event?.target?.matches('[data-current-location-search]')) {
        navigator.geolocation.getCurrentPosition(handleLocationPermissionGranted, handleLocationPermissionDenied);
    }
}

/**
 * onCurrentLocationSearch: triggers a rectangular area search so listings near by the
 * user's current location gets fetched.
 */
const onCurrentLocationSearch = () => {
    updateMapCenter({
        latitude: currentLocationSearchSettings.latitude,
        longitude: currentLocationSearchSettings.longitude
    });
    updateMinimumZoomLevel(currentLocationSearchSettings.maxZoom);
    toggleCurrentLocationSearchFlag(false);
    fetchCurrentLocationResults();
}

/**
 * Subscribing to the store$ observable to listen for changes.
 * When the map coordinates have changed, checks if the user is searching by current location.
 * If that is the case run the onCurrentLocationSearch function.
 */
store$
  .pipe(
    map(selectCurrentLocationSearchInformation),
    filter((information) => information.isCurrentLocationSearch),
    filter((information) => information.isMapReady),
    filter((information) => information.geographicCoordinates),
    distinctUntilChanged(),
    debounceTime(100)
  )
  .subscribe(onCurrentLocationSearch);

// Event delegation for current location searches. Right now only typeahead has the option to search by
// current location but we are ready in case they ask for it in another place like a search pill or similar.
  document.body.addEventListener('click', getCurrentLocation);
